/**
 * SAP Business One — ítems vía API de integración.
 *
 * - GET `integration/sapb1/items?itemCode=...` (Bearer vía `axiosInstance`) — camino de rollback.
 * - Vista previa masiva (`fetchSapB1PreviewItemsMap`): con `REACT_APP_SAP_B1_ITEMS_BATCH_ENABLED=true`
 *   usa POST `integration/sapb1/items/query` en tandas secuenciales de {@link SAP_B1_ITEMS_BATCH_WAVE_SIZE};
 *   si el batch falla (400/503/timeout) o el flag está off, cae a GET unitarios en tandas de
 *   {@link SAP_B1_ITEMS_CHUNK_SIZE} (alineado al semáforo SAP del backend, cupo compartido 8).
 * - Opcional: `REACT_APP_SAP_B1_ITEMS_SLIM=true` envía `slim=1` en el GET para que la integración
 *   pueda devolver solo precios y bodegas (menos payload desde SAP); el backend debe interpretarlo.
 */
import axiosInstance from '../utils/axiosInstance';

/**
 * Concurrencia de GET unitarios en fallback (secuencial entre tandas).
 * Mantener bajo: el semáforo backend es 8/tenant y se comparte con BP/OV.
 */
export const SAP_B1_ITEMS_CHUNK_SIZE = 4;

/** Códigos por POST `items/query` (secuencial). 120 ≈ 3 sublotes × 40 en backend; techo bajo timeout 120 s. */
export const SAP_B1_ITEMS_BATCH_WAVE_SIZE = 120;

/** Timeout del POST batch (ms). */
const SAP_B1_ITEMS_BATCH_TIMEOUT_MS = 120000;

/** @deprecated Vista previa usa tandas de {@link SAP_B1_ITEMS_CHUNK_SIZE}. */
const ENV_CONCURRENCY = Number.parseInt(String(process.env.REACT_APP_SAP_B1_ITEMS_CONCURRENCY || ''), 10);
export const SAP_B1_ITEMS_PREVIEW_CONCURRENCY = Number.isFinite(ENV_CONCURRENCY)
  ? Math.min(64, Math.max(4, ENV_CONCURRENCY))
  : SAP_B1_ITEMS_CHUNK_SIZE;

const SAP_B1_ITEMS_RETRY_BASE_MS = 500;
const SAP_B1_ITEMS_RETRY_MAX_MS = 8000;

/** Máximo de reintentos automáticos por ítem (~60 s total con backoff). */
const SAP_B1_ITEMS_MAX_RETRIES = 8;

/** @param {number} ms */
function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * @param {unknown[]} arr
 * @param {number} size
 */
function chunkArray(arr, size) {
  const n = Math.max(1, size);
  const out = [];
  for (let i = 0; i < arr.length; i += n) {
    out.push(arr.slice(i, i + n));
  }
  return out;
}

/**
 * @param {number|null|undefined} httpStatus
 * @param {string|null|undefined} errorMessage
 * @param {unknown} raw
 */
function isRetriableSapItemHttp(httpStatus, errorMessage, raw) {
  if (httpStatus != null && httpStatus >= 500) return true;
  const msg = String(errorMessage || '').toUpperCase();
  if (msg.includes('CIRCUIT_OPEN') || msg.includes('CIRCUIT BREAKER')) return true;
  try {
    const rawStr = JSON.stringify(raw ?? '').toUpperCase();
    if (rawStr.includes('CIRCUIT_OPEN')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** @param {number} attempt 1-based */
function sapItemRetryDelayMs(attempt) {
  const ms = SAP_B1_ITEMS_RETRY_BASE_MS * 2 ** Math.min(Math.max(0, attempt - 1), 5);
  return Math.min(ms, SAP_B1_ITEMS_RETRY_MAX_MS);
}

function isSapItemsBatchQueryEnabled() {
  return String(process.env.REACT_APP_SAP_B1_ITEMS_BATCH_ENABLED || '').toLowerCase() === 'true';
}

/** Params GET `integration/sapb1/items` (itemCode + opcional slim). */
function buildSapItemsGetParams(itemCode) {
  const params = { itemCode };
  if (String(process.env.REACT_APP_SAP_B1_ITEMS_SLIM || '').toLowerCase() === 'true') {
    params.slim = '1';
  }
  return params;
}

/** POST batch; si el backend no lo expone, se memoriza y no se reintenta en la sesión. */
let sapItemsQueryBatchWorks = /** @type {boolean | null} */ (null);

/**
 * @param {unknown} body
 * @returns {Record<string, SapB1ItemDto> | null} mapa por clave normalizada; null si no es un batch reconocible
 */
export function parseSapBatchItemsResponse(body) {
  const b = normalizeAxiosJsonBody(body);
  if (b == null || typeof b !== 'object') return null;

  let items = null;
  if (Array.isArray(b)) {
    items = b;
  } else if (Array.isArray(b.items)) {
    items = b.items;
  } else if (Array.isArray(b.data)) {
    const codeOk =
      typeof b.code === 'string' && b.code.trim().toUpperCase() === 'SUCCESS';
    if (codeOk || b.code == null) {
      items = b.data;
    }
  } else if (b.data && typeof b.data === 'object' && !Array.isArray(b.data) && Array.isArray(b.data.items)) {
    items = b.data.items;
  }

  if (!Array.isArray(items) || items.length === 0) {
    if (Array.isArray(items) && items.length === 0) return {};
    return null;
  }

  const map = /** @type {Record<string, SapB1ItemDto>} */ ({});
  for (const el of items) {
    if (el == null || typeof el !== 'object') continue;
    const dto =
      resolveSapItemFromApiBody({ code: 'SUCCESS', data: el }) ?? resolveSapItemFromApiBody(el);
    if (dto && typeof dto === 'object') {
      const k = normalizeSapItemCodeKey(dto.ItemCode ?? dto.itemCode);
      if (k) map[k] = dto;
    }
  }
  return map;
}

/**
 * POST batch de una tanda. `null` = falló esta tanda (usar GET fallback).
 * Probe: solo 404 apaga el batch el resto de la sesión; 400/503/timeout no.
 *
 * @param {string[]} uniqueNormalizedCodes
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ map: Record<string, SapB1ItemDto>; raw: unknown } | null>}
 */
async function tryFetchSapB1ItemsQueryBatch(uniqueNormalizedCodes, signal) {
  if (!isSapItemsBatchQueryEnabled()) return null;
  if (sapItemsQueryBatchWorks === false) return null;
  if (uniqueNormalizedCodes.length === 0) return { map: {}, raw: null };

  try {
    const response = await axiosInstance.post(
      'integration/sapb1/items/query',
      { itemCodes: uniqueNormalizedCodes },
      { signal, timeout: SAP_B1_ITEMS_BATCH_TIMEOUT_MS }
    );
    const raw = normalizeAxiosJsonBody(response?.data);
    const map = parseSapBatchResponseFromHttp(response?.data);
    if (map == null) {
      // Respuesta 200 no parseable / code distinto de SUCCESS → tratar como fallo de tanda
      return null;
    }
    sapItemsQueryBatchWorks = true;
    return { map, raw };
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') throw e;
    const status = e?.response?.status;
    if (status === 404) {
      /** Endpoint ausente: no reintentar batch en esta sesión. */
      sapItemsQueryBatchWorks = false;
    }
    /** 400 / 503 / timeout / sin response: fallback de esta tanda; no apagar probe. */
    return null;
  }
}

/** Solo pruebas: olvida si el POST por lote está disponible. */
export function resetSapB1ItemsQueryBatchProbe() {
  sapItemsQueryBatchWorks = null;
}

/** @param {unknown} data */
function parseSapBatchResponseFromHttp(data) {
  const b = normalizeAxiosJsonBody(data);
  if (b == null || typeof b !== 'object') return null;
  const code = typeof b.code === 'string' ? b.code.trim().toUpperCase() : '';
  if (code && code !== 'SUCCESS') return null;
  return parseSapBatchItemsResponse(b);
}

/**
 * Clave estable para mapas en memoria y query: quita NBSP, trim, colapsa espacios.
 * Evita que el Excel y el mapa fallen por espacios raros o claves distintas.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
export function normalizeSapItemCodeKey(raw) {
  if (raw == null) return '';
  return String(raw)
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * @typedef {object} SapB1ItemPriceRow
 * @property {number} [PriceList]
 * @property {number} [Price]
 */

/**
 * @typedef {object} SapB1ItemWarehouseRow
 * @property {string} [WarehouseCode]
 * @property {number} [InStock]
 * @property {number} [Committed]
 */

/**
 * @typedef {object} SapB1ItemDto
 * @property {string} [ItemCode]
 * @property {SapB1ItemPriceRow[]} [ItemPrices]
 * @property {SapB1ItemWarehouseRow[]} [ItemWarehouseInfoCollection]
 */

/**
 * @param {unknown} payload
 * @returns {SapB1ItemDto|null}
 */
export function unwrapSapB1ItemPayload(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const p = /** @type {{ code?: string, data?: unknown }} */ (payload);
  const codeOk =
    typeof p.code === 'string' && p.code.trim().toUpperCase() === 'SUCCESS';
  if (!codeOk || p.data == null) return null;
  let d = p.data;
  if (Array.isArray(d)) {
    if (d.length === 0 || typeof d[0] !== 'object' || d[0] == null) return null;
    d = d[0];
  }
  if (typeof d !== 'object' || d == null || Array.isArray(d)) return null;
  return /** @type {SapB1ItemDto} */ (d);
}

/** @param {object} dto */
function getItemPricesArray(dto) {
  return dto.ItemPrices ?? dto.itemPrices ?? null;
}

/** @param {object} dto */
function getWarehouseArray(dto) {
  return dto.ItemWarehouseInfoCollection ?? dto.itemWarehouseInfoCollection ?? null;
}

/**
 * Respuesta ya es el ítem (sin envoltorio { code, data }) — p. ej. gateway anida distinto.
 * @param {unknown} obj
 * @returns {SapB1ItemDto|null}
 */
function coercePlainSapItemDto(obj) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const prices = obj.ItemPrices ?? obj.itemPrices;
  const wh = obj.ItemWarehouseInfoCollection ?? obj.itemWarehouseInfoCollection;
  const ic = obj.ItemCode ?? obj.itemCode;
  if (Array.isArray(prices) || Array.isArray(wh) || ic != null) {
    return /** @type {SapB1ItemDto} */ (obj);
  }
  return null;
}

/**
 * @param {unknown} body - típicamente `response.data`
 * @returns {SapB1ItemDto|null}
 */
export function resolveSapItemFromApiBody(body) {
  let dto = unwrapSapB1ItemPayload(body);
  if (dto) return dto;
  dto = unwrapSapB1ItemPayload(body?.data);
  if (dto) return dto;
  dto = coercePlainSapItemDto(body);
  if (dto) return dto;
  dto = coercePlainSapItemDto(body?.data);
  if (dto) return dto;
  dto = coercePlainSapItemDto(body?.result);
  if (dto) return dto;
  dto = coercePlainSapItemDto(body?.payload);
  if (dto) return dto;
  const data = body?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    dto = coercePlainSapItemDto(data.item ?? data.Item ?? data.value);
    if (dto) return dto;
  }
  return null;
}

/** @param {unknown} body */
function normalizeAxiosJsonBody(body) {
  if (typeof body !== 'string') return body;
  const s = body.trim();
  if (!s) return body;
  try {
    return JSON.parse(s);
  } catch {
    return body;
  }
}

/**
 * Precio de `ItemPrices` para el número de lista indicado (p. ej. 1 Chile, 3 IX Comercio).
 *
 * @param {SapB1ItemDto|null|undefined} dto
 * @param {number} priceListId
 * @returns {number|null}
 */
export function getSapItemPriceByPriceListId(dto, priceListId) {
  if (!dto || typeof dto !== 'object') return null;
  const plId = Number(priceListId);
  if (!Number.isFinite(plId) || plId <= 0) return null;
  const arr = getItemPricesArray(dto);
  if (!Array.isArray(arr)) return null;
  const row = arr.find((x) => {
    if (!x || typeof x !== 'object') return false;
    const pl = x.PriceList ?? x.priceList;
    return Number(pl) === plId;
  });
  if (!row) return null;
  /** API puede serializar como `Price` (Pascal) o `price` (camelCase). */
  const rawPrice = row.Price ?? row.price;
  if (rawPrice == null) return null;
  const n = typeof rawPrice === 'number' ? rawPrice : parseFloat(String(rawPrice));
  return Number.isFinite(n) ? n : null;
}

/**
 * Precio lista 1 SAP (vista previa genérica / depuración).
 * Equivale a `getSapItemPriceByPriceListId(dto, 1)`.
 *
 * @param {SapB1ItemDto|null|undefined} dto
 * @returns {number|null}
 */
export function getSapItemPriceList1(dto) {
  return getSapItemPriceByPriceListId(dto, 1);
}

/**
 * Normaliza código de bodega para comparar filas del DTO (p. ej. "1" y "01" coinciden si son solo dígitos).
 *
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
export function normalizeSapWarehouseCodeForMatch(raw) {
  if (raw == null) return '';
  const s = String(raw).replace(/\u00a0/g, ' ').trim();
  if (!s) return '';
  return /^\d+$/.test(s) ? s.padStart(2, '0') : s;
}

/**
 * Stock en vista previa: **InStock − Committed** de la fila cuyo `WarehouseCode` coincide
 * (tras normalizar) con `targetWarehouseCode`. Misma regla de negocio que antes, solo
 * varía el código con el que se hace el match; sin código de bodega o sin fila → `null`.
 *
 * @param {SapB1ItemDto|null|undefined} dto
 * @param {string|number|null|undefined} targetWarehouseCode - sin valor útil (vacío) → `null`
 * @returns {number|null}
 */
export function getSapItemStockForWarehouse(dto, targetWarehouseCode) {
  if (!dto || typeof dto !== 'object') return null;
  const want = normalizeSapWarehouseCodeForMatch(targetWarehouseCode);
  if (!want) return null;
  const arr = getWarehouseArray(dto);
  if (!Array.isArray(arr)) return null;
  const w = arr.find((x) => {
    if (!x || typeof x !== 'object') return false;
    const wc = x.WarehouseCode ?? x.warehouseCode;
    if (wc == null) return false;
    return normalizeSapWarehouseCodeForMatch(wc) === want;
  });
  if (!w) return null;
  const a = Number(w.InStock ?? w.inStock);
  const b = Number(w.Committed ?? w.committed);
  const inStock = Number.isFinite(a) ? a : 0;
  const committed = Number.isFinite(b) ? b : 0;
  return inStock - committed;
}

/**
 * @deprecated Uso de pruebas y depuración; equivale a `getSapItemStockForWarehouse(dto, '01')`.
 * En producción, usar `getSapItemStockForWarehouse` con el whs de sesión.
 * @param {SapB1ItemDto|null|undefined} dto
 * @returns {number|null}
 */
export function getSapItemStockSumWarehouse01(dto) {
  return getSapItemStockForWarehouse(dto, '01');
}

/**
 * Resumen de lo que el front interpretó del DTO (útil en depuración).
 * @param {SapB1ItemDto|null|undefined} dto
 */
export function summarizeSapItemDtoForDebug(dto) {
  if (!dto || typeof dto !== 'object') {
    return { itemCode: null, priceList1: null, stockWarehouse01: null };
  }
  return {
    itemCode: dto.ItemCode ?? dto.itemCode ?? null,
    priceList1: getSapItemPriceByPriceListId(dto, 1),
    stockWarehouse01: getSapItemStockSumWarehouse01(dto)
  };
}

/**
 * Igual que GET ítem pero devuelve cuerpo crudo y metadatos (para depuración en UI).
 *
 * @param {string|number|null|undefined} itemCode
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ dto: SapB1ItemDto|null, raw: unknown, httpStatus: number|null, errorMessage: string|null, itemCode: string }>}
 */
export async function fetchSapB1ItemByItemCodeWithMeta(itemCode, options = {}) {
  const { signal } = options;
  const code = normalizeSapItemCodeKey(itemCode);
  if (!code) {
    return {
      dto: null,
      raw: null,
      httpStatus: null,
      errorMessage: 'Código vacío',
      itemCode: ''
    };
  }

  try {
    const response = await axiosInstance.get('integration/sapb1/items', {
      params: buildSapItemsGetParams(code),
      signal
    });
    const body = normalizeAxiosJsonBody(response.data);
    const dto = resolveSapItemFromApiBody(body);
    return {
      dto,
      raw: body,
      httpStatus: response.status ?? null,
      errorMessage: null,
      retryAfterSeconds: null,
      itemCode: code
    };
  } catch (e) {
    const res = /** @type {{ response?: { status?: number, data?: unknown, headers?: Record<string,string> } }} */ (e)?.response;
    const raw = res?.data != null ? normalizeAxiosJsonBody(res.data) : null;
    const retryAfterRaw = res?.headers?.['retry-after'];
    const retryAfterSeconds = retryAfterRaw ? (parseInt(retryAfterRaw, 10) || null) : null;
    return {
      dto: null,
      raw,
      httpStatus: res?.status ?? null,
      errorMessage: String(e?.message || 'Error de red'),
      retryAfterSeconds,
      itemCode: code
    };
  }
}

/**
 * @param {string|number|null|undefined} itemCode
 * @returns {Promise<SapB1ItemDto|null>}
 */
export async function fetchSapB1ItemByItemCode(itemCode) {
  const { dto } = await fetchSapB1ItemByItemCodeWithMeta(itemCode);
  return dto;
}

/**
 * GET de un ítem con reintento mientras la respuesta sea 5xx o CIRCUIT_OPEN.
 *
 * @param {string} code
 * @param {{ signal?: AbortSignal, pushMeta?: (meta: object) => void }} [options]
 */
async function fetchSapB1ItemWithRetryUntilOk(code, options = {}) {
  const { signal, pushMeta } = options;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'CanceledError';
      err.code = 'ERR_CANCELED';
      throw err;
    }
    const meta = await fetchSapB1ItemByItemCodeWithMeta(code, { signal });
    if (!isRetriableSapItemHttp(meta.httpStatus, meta.errorMessage, meta.raw)) {
      pushMeta?.(meta);
      return meta;
    }
    attempt += 1;
    if (attempt >= SAP_B1_ITEMS_MAX_RETRIES) {
      // Devolver el último error en vez de reintentar indefinidamente.
      pushMeta?.(meta);
      return meta;
    }
    // Respetar Retry-After del backend si viene; si no, usar backoff propio.
    const delayMs = meta.retryAfterSeconds
      ? meta.retryAfterSeconds * 1000
      : sapItemRetryDelayMs(attempt);
    await sleep(delayMs);
  }
}

/**
 * Rellena `map` con GET unitarios (chunk paralelo) para los códigos dados.
 *
 * @param {string[]} codes
 * @param {Record<string, SapB1ItemDto|null>} map
 * @param {{
 *   signal?: AbortSignal,
 *   chunkSize: number,
 *   pushMeta: (meta: object) => void,
 *   onChunkDone?: (n: number) => void
 * }} opts
 */
async function fillMapWithUnitGets(codes, map, opts) {
  const { signal, chunkSize, pushMeta, onChunkDone } = opts;
  const chunks = chunkArray(codes, chunkSize);
  for (const chunk of chunks) {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'CanceledError';
      err.code = 'ERR_CANCELED';
      throw err;
    }
    await Promise.all(
      chunk.map(async (code) => {
        const meta = await fetchSapB1ItemWithRetryUntilOk(code, { signal, pushMeta });
        if (signal?.aborted) return;
        map[code] = meta.dto;
      })
    );
    onChunkDone?.(chunk.length);
  }
}

/**
 * Vista previa masiva: con batch habilitado, POST `items/query` en tandas secuenciales de
 * {@link SAP_B1_ITEMS_BATCH_WAVE_SIZE}; si falla una tanda, GET unitarios solo para esos códigos.
 * Sin batch (flag off / probe 404): solo GET en tandas de {@link SAP_B1_ITEMS_CHUNK_SIZE}.
 *
 * @param {string[]} uniqueCodes - códigos ya únicos o no (se normalizan y deduplican)
 * @param {{
 *   signal?: AbortSignal,
 *   chunkSize?: number,
 *   batchWaveSize?: number,
 *   debugSampleCap?: number,
 *   onProgress?: (p: { done: number, total: number }) => void
 * }} [options]
 * @returns {Promise<{
 *   map: Record<string, SapB1ItemDto|null>,
 *   debugSamples: object[],
 *   usedBatchQuery: boolean,
 *   refetchedCount: number,
 *   totalCodes: number,
 *   batchRaw: unknown
 * }>}
 */
export async function fetchSapB1PreviewItemsMap(uniqueCodes, options = {}) {
  const {
    signal,
    chunkSize = SAP_B1_ITEMS_CHUNK_SIZE,
    batchWaveSize = SAP_B1_ITEMS_BATCH_WAVE_SIZE,
    debugSampleCap = 40,
    onProgress
  } = options;

  const normalizedList = [...new Set(uniqueCodes.map(normalizeSapItemCodeKey).filter(Boolean))];
  if (normalizedList.length === 0) {
    onProgress?.({ done: 0, total: 0 });
    return {
      map: {},
      debugSamples: [],
      usedBatchQuery: false,
      refetchedCount: 0,
      totalCodes: 0,
      batchRaw: null
    };
  }

  const debugSamples = [];
  const pushMeta = (meta) => {
    if (debugSamples.length < debugSampleCap) {
      debugSamples.push({
        itemCode: meta.itemCode,
        httpStatus: meta.httpStatus,
        errorMessage: meta.errorMessage,
        parsed: summarizeSapItemDtoForDebug(meta.dto),
        rawResponse: meta.raw
      });
    }
  };

  const map = /** @type {Record<string, SapB1ItemDto|null>} */ ({});
  let done = 0;
  let usedBatchQuery = false;
  let getCount = 0;
  /** @type {unknown} */
  let batchRaw = null;

  const bumpProgress = (n) => {
    done += n;
    onProgress?.({ done, total: normalizedList.length });
  };

  const useBatch =
    isSapItemsBatchQueryEnabled() && sapItemsQueryBatchWorks !== false;

  if (!useBatch) {
    await fillMapWithUnitGets(normalizedList, map, {
      signal,
      chunkSize,
      pushMeta,
      onChunkDone: (n) => {
        getCount += n;
        bumpProgress(n);
      }
    });
    return {
      map,
      debugSamples,
      usedBatchQuery: false,
      refetchedCount: getCount,
      totalCodes: normalizedList.length,
      batchRaw: null
    };
  }

  const waves = chunkArray(normalizedList, batchWaveSize);
  for (const wave of waves) {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'CanceledError';
      err.code = 'ERR_CANCELED';
      throw err;
    }

    const batchResult = await tryFetchSapB1ItemsQueryBatch(wave, signal);
    if (batchResult) {
      usedBatchQuery = true;
      batchRaw = batchResult.raw;
      for (const code of wave) {
        const dto = batchResult.map[code];
        map[code] = dto != null ? dto : null;
      }
      bumpProgress(wave.length);
    } else {
      await fillMapWithUnitGets(wave, map, {
        signal,
        chunkSize,
        pushMeta,
        onChunkDone: (n) => {
          getCount += n;
          bumpProgress(n);
        }
      });
    }
  }

  return {
    map,
    debugSamples,
    usedBatchQuery,
    refetchedCount: getCount,
    totalCodes: normalizedList.length,
    batchRaw
  };
}
